import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  ButtonGroup,
  Card,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useGetTenantQuery} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId, yupNumber} from 'shared/web';

import {PaymentSettings} from '../types/PaymentSettings';
import {IN_EUROPE, OUT_OF_EUROPE} from '../utils/const';

interface PaymentSettingsFormProps extends TestIdProps {
  canEditBankTransferSettings: boolean;
  defaultValues: Partial<PaymentSettings>;
  onSubmit: FormSubmitHandler<PaymentSettings>;
  onClose: VoidFunction;
}

export function PaymentSettingsForm(props: PaymentSettingsFormProps) {
  const abroadSaleOptions = [
    {label: i18n.t('entity.customerPaymentSettings.labels.saleToEU'), value: IN_EUROPE},
    {label: i18n.t('entity.customerPaymentSettings.labels.saleOutsideEU'), value: OUT_OF_EUROPE},
  ];

  const {data: tenant} = useGetTenantQuery();

  const isSubmitDisabled =
    !props.canEditBankTransferSettings &&
    !isFeatureEnabled(featureFlags.CUSTOMER_ABROAD_SALE_SETTING);

  return (
    <Form<PaymentSettings>
      defaultValues={props.defaultValues}
      onSubmit={props.onSubmit}
      schema={formSchema}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Show whenFeatureEnabled={featureFlags.CUSTOMER_ABROAD_SALE_SETTING}>
            <Card
              variant="inlineGrey"
              control={{
                type: 'switch',
                label: i18n.t('entity.customerPaymentSettings.labels.abroadSale'),
                value: formApi.watch('allowAbroadSale'),
                onChange: (value) => formApi.setValue('allowAbroadSale', value),
                'data-testid': suffixTestId('allowAbroadSale', props),
              }}
              isExpanded={formApi.watch('allowAbroadSale')}
            >
              <HStack spacing={4}>
                <FormField
                  control={control}
                  type="chips"
                  name="abroadSale"
                  isRequired
                  options={abroadSaleOptions}
                  data-testid={suffixTestId('abroadSaleOptions', props)}
                />
              </HStack>
            </Card>
          </Show>
          <Card
            variant="inlineGrey"
            control={{
              type: 'switch',
              label: i18n.t('entity.customerPaymentSettings.labels.allowBankTransfer'),
              value: formApi.watch('allowBankTransfer'),
              onChange: (value) => formApi.setValue('allowBankTransfer', value),
              isDisabled: !props.canEditBankTransferSettings,
              'data-testid': suffixTestId('allowBankTransfer', props),
            }}
            isExpanded={formApi.watch('allowBankTransfer')}
          >
            <VStack spacing={4}>
              <Grid spacing={4} columns={2}>
                <FormField
                  control={control}
                  type="integer"
                  name="invoiceMaturity"
                  label={i18n.t('entity.customerPaymentSettings.labels.invoiceMaturityDays')}
                  isRequired
                  data-testid={suffixTestId('invoiceMaturity', props)}
                  isDisabled={!props.canEditBankTransferSettings}
                />
                <FormField
                  control={control}
                  type="currency"
                  name="paymentLimit"
                  currency={isCurrency(tenant?.currency) ? tenant?.currency : undefined}
                  decimalPlaces={3}
                  label={i18n.t('entity.customerPaymentSettings.labels.paymentLimit')}
                  data-testid={suffixTestId('paymentLimit', props)}
                  isDisabled={!props.canEditBankTransferSettings}
                />
              </Grid>
              <FormField
                control={control}
                type="email"
                name="email"
                label={i18n.t('entity.customerPaymentSettings.labels.email')}
                data-testid={suffixTestId('email', props)}
                isDisabled={!props.canEditBankTransferSettings}
              />
            </VStack>
          </Card>
          <ButtonGroup align="right">
            <Button
              data-testid={suffixTestId('discard', props)}
              variant="secondary"
              onClick={props.onClose}
              title={i18n.t('general.actions.discard')}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('submit', props)}
              isDisabled={isSubmitDisabled}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const formSchema = object({
  invoiceMaturity: yupNumber.when('allowBankTransfer', {
    is: true,
    then: yupNumber.required(),
  }),
});
